/**
 * Missive detail page.
 * Shows the missive content, thread, reply form, and resource collection.
 *
 * Features a fantasy parchment aesthetic with scroll-like borders
 * and warm paper-like colors for an immersive experience.
 */

import { Head, Link, router } from '@inertiajs/react';
import { useState } from 'react';
import { Editor } from '@/components/Editor/Editor';
import { EditorRenderer } from '@/components/Editor/EditorRenderer';
import { FlashMessage } from '@/components/ui/FlashMessage';

/** Resource amounts - index signature required for Inertia form data */
interface Resources {
    [key: string]: number;
    tea: number;
    wood: number;
    mystical: number;
    medicinal: number;
    edible: number;
}

/** User reference */
interface UserRef {
    id: number;
    username: string;
}

/** Item data attached to missive */
interface ItemData {
    id: number;
    name: string;
    rarity: string;
    quality?: number;
    is_blueprint: boolean;
}

/** Missive data from the backend */
interface MissiveData {
    id: number;
    subject: string;
    content: string;
    sender: UserRef | null;
    recipient: UserRef | null;
    status: { value: string; label: string };
    resources_attached: Resources | null;
    has_resources: boolean;
    resources_collected: boolean;
    items_attached: ItemData[] | null;
    has_items: boolean;
    items_collected: boolean;
    is_read: boolean;
    is_archived: boolean;
    is_recipient: boolean;
    is_sender: boolean;
    images_allowed: boolean;
    sent_at: string | null;
    read_at: string | null;
    created_at: string;
}

/** Image permission status for the thread */
interface ImagePermissions {
    allowed: boolean;
    sender_allowed: boolean;
    recipient_allowed: boolean;
}

interface Props {
    missive: MissiveData;
    thread: MissiveData[];
    canReply: boolean;
    userResources: Resources;
    imagePermissions: ImagePermissions;
    /** Whether the current user has blocked the other party in this thread */
    isOtherPartyBlocked?: boolean;
}

/** Resource type configuration */
const RESOURCE_TYPES: { key: keyof Resources; label: string; icon: string }[] = [
    { key: 'tea', label: 'Tea', icon: '🍵' },
    { key: 'wood', label: 'Wood', icon: '🪵' },
    { key: 'mystical', label: 'Mystical', icon: '✨' },
    { key: 'medicinal', label: 'Medicinal', icon: '🌿' },
    { key: 'edible', label: 'Edible', icon: '🍎' },
];

/** Rarity color mapping for items */
const RARITY_COLORS: Record<string, string> = {
    common: 'text-slate-600 bg-slate-100 border-slate-300',
    uncommon: 'text-green-700 bg-green-100 border-green-300',
    rare: 'text-blue-700 bg-blue-100 border-blue-300',
    epic: 'text-purple-700 bg-purple-100 border-purple-300',
    legendary: 'text-amber-700 bg-amber-100 border-amber-300',
};

/** Format date in a readable format */
function formatDate(isoString: string | null): string {
    if (!isoString) return '';
    return new Date(isoString).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

/**
 * Decorative corner flourish SVG component for parchment borders.
 */
function CornerFlourish({ className = '' }: { className?: string }) {
    return (
        <svg
            className={`w-10 h-10 text-amber-800/30 ${className}`}
            viewBox="0 0 100 100"
            fill="currentColor"
        >
            <path d="M0,0 Q50,10 50,50 Q10,50 0,0 M10,0 Q40,20 40,40 Q20,40 10,0" />
        </svg>
    );
}

/**
 * Wax seal component for visual flair.
 */
function WaxSeal({ size = 'md' }: { size?: 'sm' | 'md' }) {
    const sizeClasses = size === 'sm' ? 'w-6 h-6 text-xs' : 'w-10 h-10 text-sm';
    return (
        <div
            className={`${sizeClasses} rounded-full flex items-center justify-center bg-gradient-to-br from-red-700 to-red-900`}
            style={{
                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.3)',
            }}
        >
            <span className="font-serif text-amber-100/80">✦</span>
        </div>
    );
}

/**
 * Missive Show page - displays a missive with thread and reply form.
 */
export default function Show({
    missive,
    thread,
    canReply,
    userResources,
    imagePermissions,
    isOtherPartyBlocked: initialIsOtherPartyBlocked = false,
}: Props) {
    const [showReplyForm, setShowReplyForm] = useState(false);
    const [replyContent, setReplyContent] = useState('');
    const [replyResources, setReplyResources] = useState<Resources>({
        tea: 0, wood: 0, mystical: 0, medicinal: 0, edible: 0,
    });
    const [showReplyResources, setShowReplyResources] = useState(false);
    const [allowImages, setAllowImages] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // State for blocking the other party
    const [isOtherPartyBlocked, setIsOtherPartyBlocked] = useState(initialIsOtherPartyBlocked);
    const [isBlockingOtherParty, setIsBlockingOtherParty] = useState(false);

    // Determine the other party (the person we're messaging with)
    const otherParty = missive.is_sender ? missive.recipient : missive.sender;

    /** Update a single resource amount */
    const updateResource = (key: keyof Resources, value: number) => {
        const maxValue = userResources[key];
        const clampedValue = Math.max(0, Math.min(value, maxValue));
        setReplyResources((prev) => ({ ...prev, [key]: clampedValue }));
    };

    /** Check if any resources are attached to reply */
    const hasReplyResources = Object.values(replyResources).some((v) => v > 0);

    /** Handle reply submission */
    const handleReply = (e: React.FormEvent) => {
        e.preventDefault();

        if (!replyContent.trim()) {
            setErrors({ content: 'Reply content is required' });
            return;
        }

        setIsSubmitting(true);
        setErrors({});

        router.post(`/missives/${missive.id}/reply`, {
            content: replyContent,
            allow_images: allowImages,
            resources: hasReplyResources ? replyResources : undefined,
        }, {
            onError: (errors) => {
                setErrors(errors as Record<string, string>);
                setIsSubmitting(false);
            },
            onSuccess: () => {
                setReplyContent('');
                setReplyResources({ tea: 0, wood: 0, mystical: 0, medicinal: 0, edible: 0 });
                setAllowImages(false);
                setShowReplyForm(false);
            },
            onFinish: () => {
                setIsSubmitting(false);
            },
        });
    };

    /** Handle archive */
    const handleArchive = () => {
        router.patch(`/missives/${missive.id}/archive`);
    };

    /** Handle collect resources */
    const handleCollectResources = () => {
        router.post(`/missives/${missive.id}/collect`);
    };

    /** Handle blocking/unblocking the other party */
    const handleToggleBlockOtherParty = () => {
        if (isBlockingOtherParty || !otherParty) return;

        setIsBlockingOtherParty(true);

        if (isOtherPartyBlocked) {
            // Unblock the other party
            router.delete(`/missives/blocks/${otherParty.id}`, {
                preserveScroll: true,
                onSuccess: () => {
                    setIsOtherPartyBlocked(false);
                },
                onFinish: () => {
                    setIsBlockingOtherParty(false);
                },
            });
        } else {
            // Block the other party
            router.post('/missives/blocks', {
                blocked_username: otherParty.username,
            }, {
                preserveScroll: true,
                onSuccess: () => {
                    setIsOtherPartyBlocked(true);
                },
                onFinish: () => {
                    setIsBlockingOtherParty(false);
                },
            });
        }
    };

    return (
        <>
            <Head title={missive.subject} />
            <FlashMessage />

            <div className="min-h-screen bg-slate-900 text-white">
                <div className="max-w-3xl mx-auto px-4 py-8">
                    {/* Header - kept in dark theme */}
                    <div className="flex items-center gap-4 mb-8">
                        <Link
                            href="/missives"
                            className="text-slate-300 hover:text-white transition-colors"
                        >
                            ← Back to Missives
                        </Link>
                    </div>

                    {/* Parchment scroll container */}
                    <div className="relative">
                        {/* Parchment background */}
                        <div
                            className="relative rounded-lg overflow-hidden"
                            style={{
                                background: 'linear-gradient(135deg, #f5e6d3 0%, #e8d5b7 25%, #f0e2cc 50%, #e5d4b8 75%, #f2e4c9 100%)',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.4), inset 0 0 60px rgba(139,90,43,0.15)',
                            }}
                        >
                            {/* Decorative corner flourishes */}
                            <CornerFlourish className="absolute top-2 left-2" />
                            <CornerFlourish className="absolute top-2 right-2 rotate-90" />
                            <CornerFlourish className="absolute bottom-2 left-2 -rotate-90" />
                            <CornerFlourish className="absolute bottom-2 right-2 rotate-180" />

                            {/* Aged paper edge effects */}
                            <div
                                className="absolute top-0 left-0 right-0 h-3"
                                style={{ background: 'linear-gradient(to bottom, rgba(139,90,43,0.2), transparent)' }}
                            />
                            <div
                                className="absolute bottom-0 left-0 right-0 h-3"
                                style={{ background: 'linear-gradient(to top, rgba(139,90,43,0.2), transparent)' }}
                            />

                            {/* Inner content area */}
                            <div className="relative p-6 pt-8">
                                {/* Ornate header with subject */}
                                <div className="text-center mb-6">
                                    <div className="inline-flex items-center gap-3 mb-2">
                                        <WaxSeal />
                                        <h1 className="text-2xl font-serif text-amber-900 tracking-wide">
                                            {missive.subject}
                                        </h1>
                                        <WaxSeal />
                                    </div>
                                    <p className="text-sm font-serif italic text-amber-800/60">
                                        Correspondence between{' '}
                                        <span className="text-amber-900">{missive.sender?.username || 'Unknown'}</span>
                                        {' & '}
                                        <span className="text-amber-900">{missive.recipient?.username || 'Unknown'}</span>
                                    </p>
                                </div>

                                {/* Decorative divider */}
                                <div className="flex items-center justify-center gap-2 mb-6">
                                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-800/30 to-transparent" />
                                    <span className="text-amber-800/40 text-lg">✦</span>
                                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-800/30 to-transparent" />
                                </div>

                                {/* Thread */}
                                <div className="space-y-4">
                                    {thread.map((msg, index) => (
                                        <MissiveMessage
                                            key={msg.id}
                                            message={msg}
                                            isFirst={index === 0}
                                            onCollectResources={msg.id === missive.id ? handleCollectResources : undefined}
                                        />
                                    ))}
                                </div>

                                {/* Bottom flourish */}
                                <div className="flex items-center justify-center gap-2 mt-8">
                                    <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-800/30" />
                                    <span className="text-amber-800/30 text-sm">❧</span>
                                    <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-800/30" />
                                </div>
                            </div>
                        </div>

                        {/* Shadow beneath the parchment */}
                        <div
                            className="absolute -bottom-2 left-4 right-4 h-4 rounded-full"
                            style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.3) 0%, transparent 70%)' }}
                        />
                    </div>

                    {/* Actions - styled as parchment buttons */}
                    <div className="mt-8 flex flex-wrap items-center gap-3 justify-center">
                        {canReply && !showReplyForm && (
                            <button
                                onClick={() => setShowReplyForm(true)}
                                className="cursor-pointer px-5 py-2.5 bg-gradient-to-br from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 rounded-lg font-serif font-medium transition-all shadow-lg border border-amber-500/30 text-white"
                            >
                                ✒️ Compose Reply
                            </button>
                        )}
                        {missive.is_recipient && !missive.is_archived && (
                            <button
                                onClick={handleArchive}
                                className="cursor-pointer px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors text-slate-200"
                            >
                                📦 Archive
                            </button>
                        )}
                        {/* Block/Unblock button - only show if there's another party */}
                        {otherParty && (
                            <button
                                onClick={handleToggleBlockOtherParty}
                                disabled={isBlockingOtherParty}
                                className={`cursor-pointer px-4 py-2 rounded-lg transition-colors ${
                                    isOtherPartyBlocked
                                        ? 'bg-green-600 hover:bg-green-700 text-white'
                                        : 'bg-red-600/20 hover:bg-red-600/40 text-red-400 border border-red-600/50'
                                } ${isBlockingOtherParty ? 'opacity-50 cursor-not-allowed' : ''}`}
                                title={isOtherPartyBlocked ? `Unblock ${otherParty.username}` : `Block ${otherParty.username}`}
                            >
                                {isBlockingOtherParty ? '...' : isOtherPartyBlocked ? '✓ Unblock' : '🚫 Block'}
                            </button>
                        )}
                        <ReportButton missiveId={missive.id} isRecipient={missive.is_recipient} />
                    </div>

                    {/* Reply Form - styled as parchment */}
                    {showReplyForm && (
                        <div className="relative mt-8">
                            <div
                                className="relative rounded-lg overflow-hidden"
                                style={{
                                    background: 'linear-gradient(135deg, #f5e6d3 0%, #e8d5b7 25%, #f0e2cc 50%, #e5d4b8 75%, #f2e4c9 100%)',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.4), inset 0 0 40px rgba(139,90,43,0.1)',
                                }}
                            >
                                <form onSubmit={handleReply} className="p-6">
                                    {/* Header */}
                                    <div className="flex items-center gap-3 mb-4">
                                        <span className="text-2xl">✒️</span>
                                        <h3 className="text-xl font-serif text-amber-900">Compose Your Reply</h3>
                                    </div>

                                    {/* Divider */}
                                    <div className="h-px bg-gradient-to-r from-transparent via-amber-800/30 to-transparent mb-4" />

                                    {/* Editor container */}
                                    <div className="bg-white/50 rounded-lg overflow-hidden mb-4 border border-amber-300/50">
                                        <Editor
                                            value={replyContent}
                                            onChange={(value) => setReplyContent(value)}
                                            placeholder="Write your reply..."
                                            height={150}
                                        />
                                    </div>
                                    {errors.content && (
                                        <p className="mb-4 text-sm text-red-700 font-serif">{errors.content}</p>
                                    )}

                                    {/* Resource attachment toggle */}
                                    <button
                                        type="button"
                                        onClick={() => setShowReplyResources(!showReplyResources)}
                                        className="flex items-center gap-2 text-amber-800 hover:text-amber-900 transition-colors mb-4 font-serif"
                                    >
                                        <span>💰</span>
                                        <span className="text-sm">Attach Resources as Gift</span>
                                        <span className="text-xs text-amber-700/60">
                                            {showReplyResources ? '▼' : '▶'}
                                        </span>
                                    </button>

                                    {showReplyResources && (
                                        <div className="mb-4 p-4 bg-amber-100/50 rounded-lg border border-amber-300/50">
                                            <div className="grid grid-cols-5 gap-3">
                                                {RESOURCE_TYPES.map(({ key, label, icon }) => (
                                                    <div key={key}>
                                                        <label className="block text-xs text-amber-800 mb-1 font-serif">
                                                            {icon} {label}
                                                        </label>
                                                        <input
                                                            type="number"
                                                            min={0}
                                                            max={userResources[key]}
                                                            value={replyResources[key]}
                                                            onChange={(e) => updateResource(key, parseInt(e.target.value) || 0)}
                                                            className="w-full px-2 py-1.5 bg-white/70 border border-amber-400/50 rounded text-amber-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Image permission toggle */}
                                    <div className="mb-4 p-4 bg-amber-100/50 rounded-lg border border-amber-300/50">
                                        <label className="flex items-center gap-3 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={allowImages}
                                                onChange={(e) => setAllowImages(e.target.checked)}
                                                className="w-4 h-4 rounded border-amber-400 bg-white text-amber-600 focus:ring-amber-500"
                                            />
                                            <div>
                                                <span className="text-sm text-amber-900 font-serif">🖼️ Allow images in this conversation</span>
                                                <p className="text-xs text-amber-700/70 mt-1 font-serif">
                                                    {imagePermissions.allowed ? (
                                                        <span className="text-green-700">✓ Both parties have allowed images</span>
                                                    ) : imagePermissions.sender_allowed || imagePermissions.recipient_allowed ? (
                                                        <span className="text-amber-700">⏳ Waiting for the other party to allow images</span>
                                                    ) : (
                                                        <span>Images require mutual consent from both parties</span>
                                                    )}
                                                </p>
                                            </div>
                                        </label>
                                    </div>

                                    {/* Action buttons */}
                                    <div className="flex items-center gap-3">
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="px-5 py-2.5 bg-gradient-to-br from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 disabled:from-amber-700 disabled:to-amber-800 rounded-lg font-serif font-medium transition-all shadow-lg border border-amber-500/30 text-white"
                                        >
                                            {isSubmitting ? 'Sending...' : '📤 Send Reply'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setShowReplyForm(false)}
                                            className="px-4 py-2 bg-amber-200/50 hover:bg-amber-200 text-amber-800 rounded-lg transition-colors font-serif border border-amber-400/30"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                            {/* Shadow beneath */}
                            <div
                                className="absolute -bottom-2 left-4 right-4 h-4 rounded-full"
                                style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.2) 0%, transparent 70%)' }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

/**
 * Single missive message in thread - styled as a letter/note on parchment.
 */
function MissiveMessage({
    message,
    isFirst,
    onCollectResources,
}: {
    message: MissiveData;
    isFirst: boolean;
    onCollectResources?: () => void;
}) {
    return (
        <article
            className={`relative p-5 rounded-lg transition-all ${
                message.is_sender ? 'ml-8' : 'mr-8'
            }`}
            style={{
                background: message.is_sender
                    ? 'linear-gradient(135deg, #e8dcc8 0%, #ddd0bc 100%)'
                    : 'linear-gradient(135deg, #f8f0e3 0%, #efe5d5 100%)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15), inset 0 0 30px rgba(139,90,43,0.08)',
            }}
        >
            {/* Decorative wax seal for received messages */}
            {!message.is_sender && isFirst && (
                <div className="absolute -top-2 -left-2">
                    <WaxSeal size="sm" />
                </div>
            )}

            {/* Header */}
            <div className="flex items-start justify-between mb-3">
                <div>
                    <p className="text-sm font-serif italic text-amber-800/70">
                        <span className="text-amber-900">{message.sender?.username || 'Unknown'}</span>
                        <span className="mx-2 text-amber-700/50">→</span>
                        <span className="text-amber-900">{message.recipient?.username || 'Unknown'}</span>
                    </p>
                </div>
                <span className="text-xs font-serif italic text-amber-700/60">
                    {formatDate(message.sent_at)}
                </span>
            </div>

            {/* Content - styled for parchment with handwriting font */}
            <div
                className="prose prose-sm max-w-none mb-4"
                style={{
                    fontFamily: 'var(--font-handwriting)',
                    fontSize: '1.25rem',
                    lineHeight: '1.6',
                }}
            >
                <EditorRenderer content={message.content} theme="parchment" />
            </div>

            {/* Resources */}
            {message.has_resources && message.resources_attached && (
                <ResourceBox
                    resources={message.resources_attached}
                    collected={message.resources_collected}
                    canCollect={false}
                />
            )}

            {/* Items */}
            {message.has_items && message.items_attached && (
                <ItemBox
                    items={message.items_attached}
                    collected={message.items_collected}
                    canCollect={false}
                />
            )}

            {/* Collect All Attachments Button */}
            {message.is_recipient && (
                (message.has_resources && !message.resources_collected) ||
                (message.has_items && !message.items_collected)
            ) && onCollectResources && (
                <div className="mt-3">
                    <button
                        onClick={onCollectResources}
                        className="px-4 py-2 bg-gradient-to-br from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 rounded-lg text-sm font-serif font-medium text-white transition-all shadow-md border border-amber-500/30"
                    >
                        ✋ Collect All Gifts
                    </button>
                </div>
            )}

            {/* Subtle fold line decoration */}
            <div
                className="absolute top-4 right-6 w-px h-8 opacity-20"
                style={{
                    background: 'linear-gradient(to bottom, transparent, rgba(139,90,43,0.4), transparent)',
                }}
            />
        </article>
    );
}

/**
 * Resource display box - styled as a small pouch/bundle on parchment.
 */
function ResourceBox({
    resources,
    collected,
}: {
    resources: Resources;
    collected: boolean;
    canCollect: boolean;
}) {
    const hasAny = Object.values(resources).some((v) => v > 0);
    if (!hasAny) return null;

    return (
        <div
            className={`p-3 rounded-lg border-2 mb-2 ${
                collected
                    ? 'bg-stone-200/50 border-stone-400/30'
                    : 'bg-amber-100/70 border-amber-400/50'
            }`}
            style={{
                boxShadow: collected ? 'none' : 'inset 0 1px 3px rgba(139,90,43,0.1)',
            }}
        >
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-lg">💰</span>
                    {RESOURCE_TYPES.map(({ key, label, icon }) =>
                        resources[key] > 0 ? (
                            <span
                                key={key}
                                className={`text-sm font-serif ${collected ? 'text-stone-500' : 'text-amber-800'}`}
                            >
                                {icon} {resources[key]} {label}
                            </span>
                        ) : null
                    )}
                </div>
                {collected && (
                    <span className="text-sm font-serif text-stone-500 italic">✓ Collected</span>
                )}
            </div>
        </div>
    );
}

/**
 * Item display box - shows items/blueprints attached to a missive.
 */
function ItemBox({
    items,
    collected,
}: {
    items: ItemData[];
    collected: boolean;
    canCollect: boolean;
}) {
    if (items.length === 0) return null;

    const blueprints = items.filter(i => i.is_blueprint);
    const regularItems = items.filter(i => !i.is_blueprint);

    return (
        <div
            className={`p-3 rounded-lg border-2 mb-2 ${
                collected
                    ? 'bg-stone-200/50 border-stone-400/30'
                    : 'bg-green-100/70 border-green-400/50'
            }`}
            style={{
                boxShadow: collected ? 'none' : 'inset 0 1px 3px rgba(34,139,34,0.1)',
            }}
        >
            <div className="flex flex-col gap-2">
                {/* Blueprints */}
                {blueprints.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-lg">📜</span>
                        <span className={`text-sm font-serif font-medium ${collected ? 'text-stone-500' : 'text-blue-800'}`}>
                            Blueprints:
                        </span>
                        {blueprints.map((item) => (
                            <span
                                key={item.id}
                                className={`text-sm font-serif px-2 py-0.5 rounded border ${
                                    collected ? 'bg-stone-100 border-stone-300 text-stone-500' : RARITY_COLORS[item.rarity] || RARITY_COLORS.common
                                }`}
                            >
                                {item.name}
                            </span>
                        ))}
                    </div>
                )}

                {/* Regular Items */}
                {regularItems.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-lg">🎒</span>
                        <span className={`text-sm font-serif font-medium ${collected ? 'text-stone-500' : 'text-green-800'}`}>
                            Items:
                        </span>
                        {regularItems.map((item) => (
                            <span
                                key={item.id}
                                className={`text-sm font-serif px-2 py-0.5 rounded border ${
                                    collected ? 'bg-stone-100 border-stone-300 text-stone-500' : RARITY_COLORS[item.rarity] || RARITY_COLORS.common
                                }`}
                            >
                                {item.name}
                                {item.quality && item.quality > 0 && (
                                    <span className="ml-1 text-amber-600">+{item.quality}</span>
                                )}
                            </span>
                        ))}
                    </div>
                )}

                {collected && (
                    <span className="text-sm font-serif text-stone-500 italic">✓ Collected</span>
                )}
            </div>
        </div>
    );
}

/**
 * Report button with modal - styled with parchment theme.
 */
function ReportButton({ missiveId, isRecipient }: { missiveId: number; isRecipient: boolean }) {
    const [showModal, setShowModal] = useState(false);
    const [reason, setReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!isRecipient) return null;

    const handleReport = (e: React.FormEvent) => {
        e.preventDefault();
        if (reason.trim().length < 10) return;

        setIsSubmitting(true);
        router.post(`/missives/${missiveId}/report`, { reason }, {
            onSuccess: () => {
                setShowModal(false);
                setReason('');
            },
            onFinish: () => setIsSubmitting(false),
        });
    };

    return (
        <>
            <button
                onClick={() => setShowModal(true)}
                className="px-4 py-2 text-slate-400 hover:text-red-400 transition-colors text-sm"
            >
                🚩 Report
            </button>

            {showModal && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
                    {/* Parchment-styled modal */}
                    <div
                        className="relative rounded-lg p-6 max-w-md w-full mx-4"
                        style={{
                            background: 'linear-gradient(135deg, #f5e6d3 0%, #e8d5b7 25%, #f0e2cc 50%, #e5d4b8 75%, #f2e4c9 100%)',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.5), inset 0 0 40px rgba(139,90,43,0.1)',
                        }}
                    >
                        {/* Header */}
                        <div className="flex items-center gap-3 mb-4">
                            <span className="text-2xl">🚩</span>
                            <h3 className="text-xl font-serif text-amber-900">Report This Missive</h3>
                        </div>

                        {/* Divider */}
                        <div className="h-px bg-gradient-to-r from-transparent via-amber-800/30 to-transparent mb-4" />

                        <form onSubmit={handleReport}>
                            <p className="text-sm font-serif text-amber-800/70 mb-3">
                                Please describe why you are reporting this correspondence:
                            </p>
                            <textarea
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Describe the issue (minimum 10 characters)..."
                                className="w-full px-4 py-3 bg-white/50 border border-amber-400/50 rounded-lg text-amber-900 placeholder-amber-700/40 mb-4 font-serif focus:outline-none focus:ring-2 focus:ring-amber-500"
                                rows={4}
                            />
                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 bg-amber-200/50 hover:bg-amber-200 text-amber-800 rounded-lg transition-colors font-serif border border-amber-400/30"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting || reason.trim().length < 10}
                                    className="px-4 py-2 bg-gradient-to-br from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 disabled:from-red-700 disabled:to-red-800 disabled:opacity-60 rounded-lg transition-all font-serif text-white shadow-md"
                                >
                                    {isSubmitting ? 'Submitting...' : 'Submit Report'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
}

